import { useState } from 'react';
import { useAuth } from '../../../hooks/useAuth.jsx';
import { useSettingsMutations } from '../../../hooks/useSettings.js';
import { ApiError } from '../../../lib/api.js';

/**
 * SecurityPage — change password, change email.
 *
 * Password change: requires current password verification. New password
 * must match confirmation field. Minimum 10 characters per signup rules.
 *
 * Email change: requires current password. Triggers a verification email
 * to the NEW address. Backend holds the change pending until the user
 * clicks the link in that email. Until then, log-in still works with
 * the old email.
 */

export function SecurityPage() {
  const { admin } = useAuth();
  const { changePassword, requestEmailChange } = useSettingsMutations();

  return (
    <>
      <ChangePasswordCard changePassword={changePassword} />
      <ChangeEmailCard admin={admin} requestEmailChange={requestEmailChange} />
    </>
  );
}

/* ─────────────────────────────────────────────────────── */

function ChangePasswordCard({ changePassword }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  function validate() {
    if (!currentPassword) return 'Enter your current password.';
    if (!newPassword) return 'Enter a new password.';
    if (newPassword.length < 10) return 'New password must be at least 10 characters.';
    if (newPassword !== confirmPassword) return 'New password and confirmation do not match.';
    if (newPassword === currentPassword) return 'New password must differ from current password.';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="settings-card">
      <h2 className="settings-card-title">Change password</h2>
      <p className="settings-card-body">
        Your password protects your organisation's staff data. Use at
        least 10 characters — a memorable phrase works well.
      </p>

      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {success && (
        <div className="alert alert-success" role="status">
          Password changed. You'll stay logged in on this device.
        </div>
      )}

      <div className="field">
        <label className="field-label" htmlFor="f-current-password">Current password</label>
        <input
          id="f-current-password"
          className="field-input"
          type="password"
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="f-new-password">New password</label>
        <input
          id="f-new-password"
          className="field-input"
          type="password"
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          autoComplete="new-password"
          minLength={10}
          required
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="f-confirm-password">Confirm new password</label>
        <input
          id="f-confirm-password"
          className="field-input"
          type="password"
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          minLength={10}
          required
        />
      </div>

      <div className="settings-card-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? (<><span className="spinner" aria-hidden="true" /> Changing…</>) : 'Change password'}
        </button>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────────────────── */

function ChangeEmailCard({ admin, requestEmailChange }) {
  const [newEmail, setNewEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!newEmail || !currentPassword) {
      setError('Enter the new email and your current password.');
      return;
    }
    if (newEmail === admin?.email) {
      setError("That's already your email.");
      return;
    }

    setSubmitting(true);
    try {
      await requestEmailChange(newEmail, currentPassword);
      setSuccess(true);
      setNewEmail('');
      setCurrentPassword('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not request email change.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="settings-card">
      <h2 className="settings-card-title">Change email</h2>
      <p className="settings-card-body">
        Current: <strong>{admin?.email || '—'}</strong>
        <br />
        We'll send a verification link to the new address. Your email
        won't change until you click that link — you can still log in
        with your current email in the meantime.
      </p>

      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {success && (
        <div className="alert alert-success" role="status">
          Verification email sent. Check the new inbox to confirm.
        </div>
      )}

      <div className="field">
        <label className="field-label" htmlFor="f-new-email">New email</label>
        <input
          id="f-new-email"
          className="field-input"
          type="email"
          value={newEmail}
          onChange={e => setNewEmail(e.target.value)}
          autoComplete="email"
          required
        />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="f-ce-password">Current password</label>
        <input
          id="f-ce-password"
          className="field-input"
          type="password"
          value={currentPassword}
          onChange={e => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>

      <div className="settings-card-actions">
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? (<><span className="spinner" aria-hidden="true" /> Sending…</>) : 'Request email change'}
        </button>
      </div>
    </form>
  );
}
